import React from 'react'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import styled from 'styled-components'
import type { DiagnosticsData } from '../../types/diagnostics'
import { Card } from '../common/Card'

const ChartContainer = styled(Card)`
  padding: ${({ theme }) => theme.spacing.xl};
  height: 500px;
`

const ChartTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  text-align: center;
`

const CustomTooltip = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
`

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xs};

  &:last-child {
    margin-bottom: 0;
  }
`

const TooltipLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`

const TooltipValue = styled.span`
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`

interface VO2ChartProps {
  data: DiagnosticsData[]
}

export const VO2Chart: React.FC<VO2ChartProps> = ({ data }) => {
  // Process data for the chart
  const chartData = React.useMemo(() => {
    // Group data by test date first, then sort by time within each test
    const testGroups = data.reduce(
      (groups, item) => {
        const testDate = item.test_date || 'Unknown'
        if (!groups[testDate]) {
          groups[testDate] = []
        }
        groups[testDate].push(item)
        return groups
      },
      {} as Record<string, DiagnosticsData[]>
    )

    // For now, use the most recent test or combine all data
    // In a real app, you might want to let users select which test to view
    const allTestData = Object.values(testGroups).flat()

    // Sort by time and create chart data points
    return allTestData
      .filter((item) => item.vo2 && item.time_seconds !== null)
      .sort((a, b) => a.time_seconds - b.time_seconds)
      .map((item) => ({
        time: Math.round((item.time_seconds / 60) * 100) / 100, // Convert to minutes with 2 decimal places
        timeSeconds: item.time_seconds,
        vo2: item.vo2,
        heartRate: item.heart_rate,
        phase: item.test_phase,
        speed: item.speed_kmh,
        gradient: item.treadmill_gradient,
        mets: item.mets,
        respiratoryRatio: item.respiratory_exchange_ratio,
      }))
  }, [data])

  // Find VO2 max point
  /*
  const _vo2Max = React.useMemo(() => {
    if (chartData.length === 0) return null
    return chartData.reduce((max, item) => (item.vo2 && max.vo2 && item.vo2 > max.vo2 ? item : max))
  }, [chartData])
   */

  const CustomTooltipComponent = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null

    const data = payload[0].payload

    return (
      <CustomTooltip>
        <TooltipRow>
          <TooltipLabel>Time:</TooltipLabel>
          <TooltipValue>{label} min</TooltipValue>
        </TooltipRow>
        <TooltipRow>
          <TooltipLabel>VO2:</TooltipLabel>
          <TooltipValue>{data.vo2} mL/min</TooltipValue>
        </TooltipRow>
        {data.heartRate && (
          <TooltipRow>
            <TooltipLabel>Heart Rate:</TooltipLabel>
            <TooltipValue>{data.heartRate} bpm</TooltipValue>
          </TooltipRow>
        )}
        {data.phase && (
          <TooltipRow>
            <TooltipLabel>Phase:</TooltipLabel>
            <TooltipValue>{data.phase}</TooltipValue>
          </TooltipRow>
        )}
        {data.speed && (
          <TooltipRow>
            <TooltipLabel>Speed:</TooltipLabel>
            <TooltipValue>{data.speed} km/h</TooltipValue>
          </TooltipRow>
        )}
        {data.mets && (
          <TooltipRow>
            <TooltipLabel>METs:</TooltipLabel>
            <TooltipValue>{data.mets}</TooltipValue>
          </TooltipRow>
        )}
      </CustomTooltip>
    )
  }

  if (chartData.length === 0) {
    return (
      <ChartContainer>
        <ChartTitle>VO2 vs Time</ChartTitle>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '300px',
            color: '#666',
          }}
        >
          No VO2 data available for visualization
        </div>
      </ChartContainer>
    )
  }

  return (
    <ChartContainer>
      <ChartTitle>VO2 vs Time</ChartTitle>
      <ResponsiveContainer width="100%" height="90%">
        <LineChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 60,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
          <XAxis
            dataKey="time"
            type="number"
            scale="linear"
            domain={['dataMin', 'dataMax']}
            tick={{ fontSize: 12, fill: '#9CA3AF' }}
            axisLine={{ stroke: '#6B7280' }}
            tickLine={{ stroke: '#6B7280' }}
          />
          <YAxis
            domain={['dataMin - 100', 'dataMax + 100']}
            tick={{ fontSize: 12, fill: '#9CA3AF' }}
            axisLine={{ stroke: '#6B7280' }}
            tickLine={{ stroke: '#6B7280' }}
          />
          <Tooltip content={<CustomTooltipComponent />} />
          <Legend
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '14px',
            }}
          />

          {/* Main VO2 line */}
          <Line
            type="monotone"
            dataKey="vo2"
            stroke="#3B82F6"
            strokeWidth={2}
            dot={{ fill: '#6B7280', strokeWidth: 2, r: 3 }}
            activeDot={{ r: 6, stroke: '#6B7280', strokeWidth: 2 }}
            name="VO2 (mL/min)"
          />

          {/* VO2 Max reference line
          {vo2Max && (
            <ReferenceLine
              y={vo2Max.vo2}
              stroke="#EF4444"
              strokeDasharray="5 5"
              strokeWidth={2}
              label={{
                value: `VO2 Max: ${vo2Max.vo2} mL/min`,
                position: 'top',
                style: { fill: '#EF4444', fontSize: '12px', fontWeight: 'bold' }
              }}
            />
          )} */}
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}
